import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, CheckCircle2, Bot, Calendar, Bell, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dismissNewUserModal } from '@/lib/api/user-settings';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useTour } from '@/lib/TourProvider';

interface OnboardingFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface OnboardingStage {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  details: string[];
}

const stages: OnboardingStage[] = [
  {
    id: 'welcome',
    title: 'Welcome to OneGov',
    description: 'Your AI-powered legislative intelligence platform',
    icon: <CheckCircle2 className="h-12 w-12 text-[#9c8053]" />,
    details: [
      'Track bills automatically with AI',
      'Monitor committee meetings and hearings',
      'Get personalized daily updates',
      'Never miss important legislative changes',
    ],
  },
  {
    id: 'agents',
    title: 'Meet Your AI Agents',
    description: 'Two powerful agents working for you 24/7',
    icon: <Bot className="h-12 w-12 text-[#1e3a8a]" />,
    details: [
      '**Committee Agent**: Tracks upcoming meetings, identifies bills you care about, and finds testimony opportunities',
      '**Bill Agent**: Auto-tracks legislation, analyzes passage probability, and prioritizes high-impact bills',
    ],
  },
  {
    id: 'tracking',
    title: 'Start Tracking Bills',
    description: 'Add bills to your tracking list to get started',
    icon: <Search className="h-12 w-12 text-[#9c8053]" />,
    details: [
      'Search for bills by number, keyword, or topic',
      'Set up auto-tracking with custom keywords',
      'Bills matching your criteria are tracked automatically',
      'Review and organize in your dashboard',
    ],
  },
  {
    id: 'calendar',
    title: 'Stay on Top of Events',
    description: 'Track committee meetings, hearings, and deadlines',
    icon: <Calendar className="h-12 w-12 text-[#1e3a8a]" />,
    details: [
      'View upcoming legislative calendar events',
      'Get notified about meetings for your tracked bills',
      'Filter by state and event type',
      'Never miss a critical hearing or deadline',
    ],
  },
  {
    id: 'alerts',
    title: 'Configure Your Alerts',
    description: 'Customize how you receive updates',
    icon: <Bell className="h-12 w-12 text-amber-500" />,
    details: [
      'Set notification preferences for bill changes',
      'Get alerts for committee activity',
      'Receive daily legislative summaries',
      'All notifications accessible from your dashboard',
    ],
  },
];

export function OnboardingFlowModal({ isOpen, onClose }: OnboardingFlowModalProps) {
  const [currentStage, setCurrentStage] = useState(0);
  const { refetchUser } = useAuth();
  const { toast } = useToast();
  const { startTour } = useTour();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = async () => {
    if (currentStage < stages.length - 1) {
      setCurrentStage(currentStage + 1);
    } else {
      // Mark onboarding as complete via API
      setIsSubmitting(true);
      try {
        await dismissNewUserModal();
        // Set localStorage as backup to prevent re-showing during refetch
        localStorage.setItem('onboarding-completed', 'true');
        await refetchUser();
        onClose();
        // Start the interactive tour after a delay to ensure dashboard is fully rendered
        setTimeout(() => {
          startTour();
        }, 1000);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to complete onboarding. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStage > 0) {
      setCurrentStage(currentStage - 1);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    try {
      await dismissNewUserModal();
      // Set localStorage as backup to prevent re-showing during refetch
      localStorage.setItem('onboarding-completed', 'true');
      await refetchUser();
      toast({
        title: 'Tour skipped',
        description: 'You can explore the platform on your own.',
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to skip tour. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stage = stages[currentStage];
  const isLastStage = currentStage === stages.length - 1;

  return (
    <DialogPrimitive.Root open={isOpen} modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 grid w-full max-w-2xl max-h-[90vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2',
            'data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'sm:rounded-lg',
          )}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="py-6">
            {/* Progress indicators */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {stages.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'h-2 rounded-full transition-all duration-300',
                    index === currentStage
                      ? 'w-8 bg-gradient-to-r from-[#9c8053] to-[#8a704a]'
                      : index < currentStage
                        ? 'w-2 bg-green-500'
                        : 'w-2 bg-gray-200',
                  )}
                />
              ))}
            </div>

            {/* Stage content */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">{stage.icon}</div>
              <h2 className="text-2xl font-serif font-bold text-foreground mb-2">{stage.title}</h2>
              <p className="text-muted-foreground">{stage.description}</p>
            </div>

            {/* Stage details */}
            <div className="bg-gradient-to-br from-white to-[#9c8053]/5 border border-[#9c8053]/15 rounded-xl p-6 mb-8">
              <ul className="space-y-3">
                {stage.details.map((detail, index) => {
                  // Check if detail contains markdown bold syntax
                  const parts = detail.split('**');
                  return (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground">
                        {parts.length > 1 ? (
                          <>
                            <strong>{parts[1]}</strong>
                            {parts[2]}
                          </>
                        ) : (
                          detail
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-end">
              <div className="flex items-center gap-2">
                {currentStage > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={isSubmitting}
                    className="gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className="gap-2 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#1e3a8a]"
                >
                  {isLastStage ? 'Get Started' : 'Next'}
                  {!isLastStage && <ChevronRight className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
